/**
 * Copyright (c) 2022–2026 Podstack.ai
 * All rights reserved.
 * Proprietary and Confidential.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vmApi } from '../services/vmApi';
import { VM, VMCreateRequest } from '../types/vm';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

export interface UseVMsParams {
  projectId?: string;
  status?: string;
  page?: number;
  size?: number;
  enabled?: boolean; // Allow disabling the query (useful for feature flags)
}

export const useVMs = (params: UseVMsParams = {}) => {
  const { projectId, status, page = 1, size = 20, enabled = true } = params;
  const queryClient = useQueryClient();

  // List VMs query with production-grade caching
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['vms', projectId, status, page, size],
    queryFn: () => vmApi.listVMs({ project_id: projectId, status, page, size }),
    enabled: enabled, // Always enabled (projectId is optional for listing all VMs)
    staleTime: 0, // Always consider data stale to ensure fresh data after mutations
    gcTime: 5 * 60 * 1000, // Keep unused data in cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus to reduce unnecessary calls
    refetchOnMount: true, // Always refetch on mount to ensure fresh data
  });

  const vms: VM[] = data?.vms || [];
  const runningVMs = vms.filter((v) => v.status === 'running');
  const stoppedVMs = vms.filter((v) => v.status === 'stopped');

  // Create VM mutation with immediate UI update
  const createMutation = useMutation({
    mutationFn: (vmData: VMCreateRequest) => vmApi.createVM(vmData, projectId),
    onSuccess: async () => {
      // Force immediate refetch of all VM queries to update UI instantly
      await queryClient.invalidateQueries({ queryKey: ['vms'], refetchType: 'active' });
      toast.success('VM creation initiated');
    },
    onError: (error: AxiosError<{ detail?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to create VM';
      toast.error(errorMessage);
    },
  });

  // Start VM mutation with immediate UI update
  const startMutation = useMutation({
    mutationFn: (vmId: string) => vmApi.startVM(vmId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vms'], refetchType: 'active' });
      toast.success('VM start initiated');
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      toast.error(error.response?.data?.detail || 'Failed to start VM');
    },
  });

  // Stop VM mutation with immediate UI update
  const stopMutation = useMutation({
    mutationFn: (vmId: string) => vmApi.stopVM(vmId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vms'], refetchType: 'active' });
      toast.success('VM stop initiated');
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      toast.error(error.response?.data?.detail || 'Failed to stop VM');
    },
  });

  // Restart VM mutation with immediate UI update
  const restartMutation = useMutation({
    mutationFn: (vmId: string) => vmApi.restartVM(vmId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vms'], refetchType: 'active' });
      toast.success('VM restart initiated');
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      toast.error(error.response?.data?.detail || 'Failed to restart VM');
    },
  });

  // Delete VM mutation with immediate UI update
  const deleteMutation = useMutation({
    mutationFn: (vmId: string) => vmApi.deleteVM(vmId),
    onSuccess: async () => {
      // Force immediate refetch of all VM queries to update UI instantly
      await queryClient.invalidateQueries({ queryKey: ['vms'], refetchType: 'active' });
      toast.success('VM deletion initiated');
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      toast.error(error.response?.data?.detail || 'Failed to delete VM');
    },
  });

  // Retry VM creation mutation (for VMs in ERROR state)
  const retryMutation = useMutation({
    mutationFn: (vmId: string) => vmApi.retryVM(vmId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vms'], refetchType: 'active' });
      toast.success('VM creation retry initiated');
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      toast.error(error.response?.data?.detail || 'Failed to retry VM creation');
    },
  });

  // Sync VM status mutation (for status reconciliation)
  const syncStatusMutation = useMutation({
    mutationFn: (vmId: string) => vmApi.syncVMStatus(vmId),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['vms'], refetchType: 'active' });
      if (data.synced) {
        toast.success(data.message || 'VM status synced successfully');
      } else {
        toast.error(data.message || 'Failed to sync VM status');
      }
    },
    onError: (error: AxiosError<{ detail?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to sync VM status';
      toast.error(errorMessage);
    },
  });

  return {
    vms,
    runningVMs,
    stoppedVMs,
    runningCount: runningVMs.length,
    stoppedCount: stoppedVMs.length,
    totalCount: data?.total || 0,
    page: data?.page || page,
    size: data?.size || size,
    pages: data?.pages || 1,
    isLoading,
    isFetching, // Add isFetching for better loading state management
    isError,
    refetch,
    createVM: createMutation.mutate,
    isCreating: createMutation.isPending,
    startVM: startMutation.mutate,
    isStarting: startMutation.isPending,
    stopVM: stopMutation.mutate,
    isStopping: stopMutation.isPending,
    restartVM: restartMutation.mutate,
    isRestarting: restartMutation.isPending,
    deleteVM: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    retryVM: retryMutation.mutate,
    isRetrying: retryMutation.isPending,
    syncStatus: syncStatusMutation.mutate,
    isSyncingStatus: syncStatusMutation.isPending,
  };
};
